<template>
  <div>
    <div class="mb-4">
      <h3>{{ $t("purchased_this_product") }}</h3>
      <p class="text-sm text-gray-500">
        {{ $t("drop_valuable_review") }}
      </p>
    </div>

    <!-- Allow user to submit review only of profile is complete -->
    <VForm
      v-if="isAuthUserProfileComplete"
      :id="id"
      ref="form"
      :fields="fields"
      :get="get"
      :save="save"
      :delete="del"
    >
      <template
        #default="{
          state,
          saveItem,
          hasError,
          error,
          fieldErrors,
          nonFieldErrors,
          isSaving,
          isGetting,
          isDeleting,
          deleteItem,
        }"
      >
        <ul v-if="isGetting" class="space-y-4 animate-pulse">
          <li>
            <div class="w-24 h-3 rounded-md bg-shimmer"></div>
            <div class="h-8 mt-2 rounded-md bg-shimmer"></div>
          </li>
          <li>
            <div class="w-24 h-3 rounded-md bg-shimmer"></div>
            <div class="h-48 mt-2 rounded-md bg-shimmer"></div>
          </li>
          <li>
            <div class="w-24 h-8 rounded-md bg-shimmer"></div>
          </li>
        </ul>

        <Form v-else ref="form" tag="div" class="space-y-4" @submit="saveItem">
          <!-- Errors -->
          <FormError v-if="hasError" :value="nonFieldErrors" :error="error" />

          <FormField
            :label="$t('your_rating')"
            :error="fieldErrors.rating"
            rules="required"
          >
            <FormRating v-model="state.rating" name="rating" />
          </FormField>

          <FormField
            :label="$t('your_review')"
            :error="fieldErrors.review"
            rules="required"
          >
            <FormTextarea
              v-model="state.review"
              rules="required"
              rows="6"
              name="review"
            />
          </FormField>

          <div class="flex items-center justify-between">
            <VyButton
              type="submit"
              color="primary"
              size="md"
              shape="rounded"
              theme="solid"
              :label="$t('submit')"
              :loading="isSaving"
            />

            <VyButton
              color="danger"
              size="md"
              shape="squircle"
              theme="muted"
              title="Delete Review"
              icon="MdiDelete"
              :loading="isDeleting"
              @click="deleteItem()"
            />
          </div>
        </Form>
      </template>
    </VForm>

    <div v-else class="pt-4 border-t">
      <p class="text-sm text-warning-500">
        {{ $t("product_review_complete_profile") }}
      </p>
      <VyButton
        class="mt-2"
        color="primary"
        size="md"
        shape="rounded"
        theme="solid"
        direction="rowReverse"
        icon="MdiArrowRight"
        :label="$t('complete_profile_now')"
        :to="{
          name: 'biz-account-profile',
          params: {
            redirect: { route: $route.name, params: { slug: productSlug } },
          },
        }"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from "pinia";
import upsert from "@/graph/store/review/upsert.gql";
import del from "@/graph/store/review/delete.gql";
import get from "@/graph/store/review/get.gql";
import { useVepaarStore } from "@/store/store.js";
import { useProductStore } from "@/store/product.js";
import { useAuthStore } from "@/store/auth.js";

export default {
  props: {
    id: {
      type: Number,
      default: null,
    },
  },
  emits: ["refresh"],

  setup() {
    const productStore = useProductStore();
    const productId = computed(() => productStore.id);
    const productSlug = computed(() => productStore?.product.slug);
    return { productId, productSlug };
  },

  data() {
    return {
      fields: ["rating", "review"],
    };
  },

  computed: {
    ...mapState(useVepaarStore, ["hash"]),
    ...mapState(useAuthStore, ["isAuthUserProfileComplete"]),
  },

  methods: {
    get(id) {
      if (id) {
        const { clients } = useApollo();
        return clients.default
          .query({
            query: get,
            variables: {
              id,
              productId: this.productId,
              hash: this.hash,
            },
          })
          .then(({ data }) => {
            return data.productReview;
          });
      } else {
        return new Promise((resolve) => resolve());
      }
    },

    async save(id, input) {
      const { clients } = useApollo();
      return clients.default
        .mutate({
          mutation: upsert,
          variables: {
            id,
            input,
            productId: this.productId,
            hash: this.hash,
          },
        })
        .then((res) => {
          this.$nuxt.callHook("modal-close", "modal-review");
          this.$emit("refresh");
          return res;
        });
    },

    del(id) {
      const isConfirm = confirm("Are you sure you want to delete review?");
      if (!isConfirm) return;

      const { clients } = useApollo();
      return clients.default
        .mutate({
          mutation: del,
          variables: {
            id,
            productId: this.productId,
            hash: this.hash,
          },
        })
        .then((res) => {
          this.$nuxt.callHook("modal-close", "modal-review");
          this.$emit("refresh");
          return res;
        });
    },
  },
};
</script>
